import { Device } from "api/types/device";
import { t } from "i18next";
import { CONST } from "models/constants";
import { DeviceConfiguration } from "models/devices/configuration";
import LabeledField from "providers/labeledField";
import { useState } from "react";
import { FaEdit } from "react-icons/fa";
import { MdPerson, MdGroupAdd, MdOutlineHub } from "react-icons/md";
import GenericDialog from "widgets/dialogs/generic";
import DeviceCard from "widgets/cards/device";
import DeviceConfigurationDialog from "widgets/dialogs/deviceConfiguration";
import RecipientsDialog from "widgets/dialogs/recipients";
import CircleButton from "widgets/controls/buttons/circle-button";
import DeviceManageChildrenDialog from "widgets/dialogs/deviceManageChildren";

interface GeneralProps {
  device?: Device;
  config?: DeviceConfiguration;
  onUpdate?: () => void;
}

const Configuration: React.FC<GeneralProps> = ({
  device,
  config,
  onUpdate,
}) => {
  const [showDeviceConfiguration, setShowDeviceConfiguration] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);
  const [showManageChildren, setShowManageChildren] = useState(false);

  const handleClose = (refresh: boolean) => {
    setShowRecipients(false);
    setShowDeviceConfiguration(false);
    setShowManageChildren(false);
    if (refresh) {
      onUpdate();
    }
  };

  return (
    <>
      {showRecipients && (
        <RecipientsDialog
          id={device.snr}
          noLevels={device?.escalationLevels}
          values={device.recipients}
          isOpen={showRecipients}
          onClose={() => handleClose(false)}
          onSuccess={() => handleClose(true)}
        />
      )}
      {showDeviceConfiguration && (
        <DeviceConfigurationDialog
          device={device}
          config={config}
          isOpen={showDeviceConfiguration}
          onClose={() => handleClose(false)}
          onSuccess={() => handleClose(true)}
        />
      )}
      {showManageChildren && (
        <DeviceManageChildrenDialog
          device={device}
          isOpen={showManageChildren}
          onClose={() => handleClose(false)}
          onSuccess={() => handleClose(true)}
        />
      )}
      <DeviceCard
        title={t("device.configuration")}
        config={config}
        topRight={
          <div className="-mt-2 flex">
            <CircleButton
              icon={MdPerson}
              size="md"
              onClick={() => setShowRecipients(true)}
              variant="ghost"
              permissionRole={CONST.ROLE_OWNER}
              tooltip={t("tooltip.recipients")}
              tooltipPlacement="bottom"
              color="white"
            />
            <CircleButton
              icon={MdOutlineHub}
              size="md"
              onClick={() => setShowManageChildren(true)}
              variant="ghost"
              permissionRole={CONST.ROLE_OWNER}
              tooltip={t("tooltip.manageChildren")}
              tooltipPlacement="bottom"
              color="white"
            />
            {config.type !== "multi-line" && (
              <CircleButton
                icon={FaEdit}
                size="md"
                variant="ghost"
                onClick={() => {
                  setShowDeviceConfiguration(true);
                }}
                permissionRole={CONST.ROLE_OWNER}
                tooltip={t("tooltip.edit.configuration")}
                tooltipPlacement="bottom"
                color="white"
              />
            )}
          </div>
        }
      >
        {config &&
          config.type !== "multi-line" &&
          (config.units === "water" ||
            config.units === "electricity" ||
            config.units === "gas") && (
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-5">
              <LabeledField
                className="text-txt-xs"
                textColor="white"
                label={t("form.input.daily.limit", { context: config.units })}
                id={"config-field-one"}
              >
                <div className="text-txt-lg text-white">
                  {device?.dailyUsageLimit || t("label.limit")}
                </div>
              </LabeledField>
            </div>
          )}

        {config &&
          (config.units === "temperature" || config.units === "humidity") && (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-5">
            <LabeledField
              className="text-txt-xs"
              textColor="white"
              label={t("form.input.trigger.low", { context: config.units })}
              id={"config-field-one"}
            >
              <div className="text-txt-lg text-white">
                {device?.triggerLow || t("label.high")}
              </div>
            </LabeledField>
            <LabeledField
              className="text-txt-xs"
              textColor="white"
              label={t("form.input.trigger.high", { context: config.units })}
              id={"config-field-two"}
            >
              <div className="text-txt-lg text-white">
                {device?.triggerHigh || t("label.low")}
              </div>
            </LabeledField>
            <LabeledField
              className="text-txt-xs"
              textColor="white"
              label={t("form.input.missing.time")}
              id={"config-field-three"}
            >
              <div className="text-txt-lg text-white">
                {device?.missingTime || t("label.none")}
              </div>
            </LabeledField>
          </div>
        )}

        {config && config.units === "level" && (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-5">
            <LabeledField
              className="text-txt-xs"
              textColor="white"
              label={t("form.input.daily.limit", { context: config.units })}
              id={"config-field-one"}
            >
              <div className="text-txt-lg text-white">
                {device?.lowLevelPercentage || t("label.limit")}
              </div>
            </LabeledField>
          </div>
        )}
      </DeviceCard>
    </>
  );
};

export default Configuration;
